"use strict";
/**
 * Nexus 消息通信系统
 * 简化插件与 Cocos Creator 编辑器的通信
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.NexusMessage = void 0;
exports.Message = Message;
exports.registerMessageHandlers = registerMessageHandlers;
/**
 * 消息通信管理器
 */
class NexusMessage {
    /**
     * 发送请求消息到编辑器
     * @param message 消息名称
     * @param args 消息参数
     * @returns Promise<any>
     */
    static async request(message, ...args) {
        return new Promise((resolve, reject) => {
            const timeout = 5000;
            try {
                // 使用 Editor.Message.request 发送请求
                if (typeof Editor !== 'undefined' && Editor.Message && Editor.Message.request) {
                    const timeoutHandle = setTimeout(() => {
                        reject(new Error(`消息 ${message} 超时`));
                    }, timeout);
                    Editor.Message.request(message, ...args)
                        .then((result) => {
                        clearTimeout(timeoutHandle);
                        resolve(result);
                    })
                        .catch((error) => {
                        clearTimeout(timeoutHandle);
                        reject(error);
                    });
                }
                else {
                    // 开发环境模拟
                    console.log(`[Nexus] 请求消息: ${message}`, args);
                    setTimeout(() => {
                        resolve({ success: true, message: '开发环境模拟响应', args });
                    }, 100);
                }
            }
            catch (error) {
                reject(error);
            }
        });
    }
    /**
     * 发送消息到编辑器（不等待响应）
     * @param message 消息名称
     * @param data 消息数据
     */
    static send(message, data) {
        try {
            if (typeof Editor !== 'undefined' && Editor.Message && Editor.Message.send) {
                Editor.Message.send(message, data);
            }
            else {
                console.log(`[Nexus] 发送消息: ${message}`, data);
            }
        }
        catch (error) {
            console.error('发送消息失败:', error);
        }
    }
    /**
     * 广播消息到所有面板
     * @param message 消息名称
     * @param data 消息数据
     */
    static broadcast(message, data) {
        try {
            if (typeof Editor !== 'undefined' && Editor.Message) {
                Editor.Message.broadcast(message, data);
            }
            else {
                console.log(`[Nexus] 广播消息: ${message}`, data);
            }
        }
        catch (error) {
            console.error('广播消息失败:', error);
        }
    }
    /**
     * 注册消息处理器
     * @param message 消息名称
     * @param handler 处理函数
     */
    static on(message, handler) {
        if (!this.handlers.has(message)) {
            this.handlers.set(message, []);
        }
        this.handlers.get(message).push(handler);
    }
    /**
     * 移除消息处理器
     * @param message 消息名称
     * @param handler 处理函数
     */
    static off(message, handler) {
        if (!this.handlers.has(message))
            return;
        if (handler) {
            const handlers = this.handlers.get(message);
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
        else {
            this.handlers.delete(message);
        }
    }
    /**
     * 处理接收到的消息
     * @param message 消息名称
     * @param data 消息数据
     */
    static async handle(message, data) {
        const handlers = this.handlers.get(message);
        if (!handlers || handlers.length === 0) {
            console.warn(`没有找到消息 ${message} 的处理器`);
            return;
        }
        try {
            const results = await Promise.all(handlers.map(handler => handler(data)));
            return results.length === 1 ? results[0] : results;
        }
        catch (error) {
            console.error(`处理消息 ${message} 时出错:`, error);
            throw error;
        }
    }
}
exports.NexusMessage = NexusMessage;
NexusMessage.handlers = new Map();
NexusMessage.messageId = 0;
NexusMessage.pendingMessages = new Map();
/**
 * 场景相关操作
 */
NexusMessage.Scene = {
    /**
     * 获取当前场景信息
     */
    async getCurrentScene() {
        return NexusMessage.request('scene:query-current-scene');
    },
    /**
     * 获取场景中的所有节点
     */
    async getAllNodes() {
        return NexusMessage.request('scene:query-all-nodes');
    },
    /**
     * 根据UUID获取节点
     */
    async getNodeByUUID(uuid) {
        return NexusMessage.request('scene:query-node', { uuid });
    },
    /**
     * 创建新节点
     */
    async createNode(name, parent) {
        return NexusMessage.request('scene:create-node', { name, parent });
    },
    /**
     * 删除节点
     */
    async deleteNode(uuid) {
        return NexusMessage.request('scene:delete-node', { uuid });
    },
    /**
     * 选中节点
     */
    async selectNode(uuid) {
        return NexusMessage.request('scene:select-node', { uuid });
    },
    /**
     * 场景操作
     */
    /**
     * 打开场景
     */
    async openScene(sceneUuid) {
        return NexusMessage.request('scene', 'open-scene', sceneUuid);
    },
    /**
     * 保存场景
     */
    async saveScene() {
        return NexusMessage.request('scene', 'save-scene');
    },
    /**
     * 另存为场景
     */
    async saveAsScene() {
        return NexusMessage.request('scene', 'save-as-scene');
    },
    /**
     * 关闭场景
     */
    async closeScene() {
        return NexusMessage.request('scene', 'close-scene');
    },
    /**
     * 属性操作
     */
    /**
     * 设置对象属性
     */
    async setProperty(options) {
        return NexusMessage.request('scene', 'set-property', {
            uuid: options.uuid,
            path: options.path,
            dump: options.dump
        });
    },
    /**
     * 重置对象属性为默认值
     */
    async resetProperty(options) {
        return NexusMessage.request('scene', 'reset-property', {
            uuid: options.uuid,
            path: options.path
        });
    },
    /**
     * 数组操作
     */
    /**
     * 移动数组元素位置
     */
    async moveArrayElement(options) {
        return NexusMessage.request('scene', 'move-array-element', {
            uuid: options.uuid,
            path: options.path,
            target: options.target,
            offset: options.offset
        });
    },
    /**
     * 移除数组元素
     */
    async removeArrayElement(options) {
        return NexusMessage.request('scene', 'remove-array-element', {
            uuid: options.uuid,
            path: options.path,
            index: options.index
        });
    },
    /**
     * 节点操作
     */
    /**
     * 复制节点
     */
    async copyNode(uuids) {
        return NexusMessage.request('scene', 'copy-node', uuids);
    },
    /**
     * 复制节点
     */
    async duplicateNode(uuids) {
        return NexusMessage.request('scene', 'duplicate-node', uuids);
    },
    /**
     * 粘贴节点
     */
    async pasteNode(options) {
        return NexusMessage.request('scene', 'paste-node', {
            target: options.target,
            uuids: options.uuids,
            keepWorldTransform: options.keepWorldTransform
        });
    },
    /**
     * 场景事件监听
     */
    /**
     * 监听场景打开事件
     */
    onSceneReady(callback) {
        NexusMessage.on('scene:ready', callback);
    },
    /**
     * 监听场景关闭事件
     */
    onSceneClose(callback) {
        NexusMessage.on('scene:close', callback);
    },
    /**
     * 监听光照探针编辑模式变化事件
     */
    onLightProbeEditModeChanged(callback) {
        NexusMessage.on('scene:light-probe-edit-mode-changed', callback);
    },
    /**
     * 监听光照探针包围盒编辑模式变化事件
     */
    onLightProbeBoundingBoxEditModeChanged(callback) {
        NexusMessage.on('scene:light-probe-bounding-box-edit-mode-changed', callback);
    },
    /**
     * 更多节点操作
     */
    /**
     * 剪切节点
     */
    async cutNode(uuids) {
        return NexusMessage.request('scene', 'cut-node', uuids);
    },
    /**
     * 设置节点父级
     */
    async setParent(options) {
        return NexusMessage.request('scene', 'set-parent', {
            parent: options.parent,
            uuids: options.uuids,
            keepWorldTransform: options.keepWorldTransform
        });
    },
    /**
     * 创建节点（高级版本）
     */
    async createNodeAdvanced(options) {
        return NexusMessage.request('scene', 'create-node', {
            name: options.name || 'New Node',
            parent: options.parent,
            components: options.components,
            dump: options.dump,
            keepWorldTransform: options.keepWorldTransform,
            type: options.type,
            unlinkPrefab: options.unlinkPrefab,
            assetUuid: options.assetUuid
        });
    },
    /**
     * 重置节点（位置、旋转、缩放）
     */
    async resetNode(uuid) {
        return NexusMessage.request('scene', 'reset-node', {
            uuid: uuid
        });
    },
    /**
     * 重置组件
     */
    async resetComponent(uuid) {
        return NexusMessage.request('scene', 'reset-component', {
            uuid: uuid
        });
    },
    /**
     * 从资源恢复预制体节点
     */
    async restorePrefab(nodeUuid, assetUuid) {
        return NexusMessage.request('scene', 'restore-prefab', nodeUuid, assetUuid);
    },
    /**
     * 移除节点
     */
    async removeNode(uuids) {
        return NexusMessage.request('scene', 'remove-node', {
            uuid: uuids
        });
    },
    /**
     * 组件操作
     */
    /**
     * 创建组件
     */
    async createComponent(options) {
        return NexusMessage.request('scene', 'create-component', {
            uuid: options.uuid,
            component: options.component
        });
    },
    /**
     * 移除组件
     */
    async removeComponent(options) {
        return NexusMessage.request('scene', 'remove-component', {
            uuid: options.uuid,
            component: options.component
        });
    },
    /**
     * 执行操作
     */
    /**
     * 执行组件方法
     */
    async executeComponentMethod(options) {
        return NexusMessage.request('scene', 'execute-component-method', {
            uuid: options.uuid,
            name: options.name,
            args: options.args
        });
    },
    /**
     * 执行扩展脚本方法
     */
    async executeSceneScript(options) {
        return NexusMessage.request('scene', 'execute-scene-script', {
            name: options.name,
            method: options.method,
            args: options.args
        });
    },
    /**
     * 快照和撤销操作
     */
    /**
     * 创建当前场景状态快照
     */
    async snapshot() {
        return NexusMessage.request('scene', 'snapshot');
    },
    /**
     * 中止快照
     */
    async snapshotAbort() {
        return NexusMessage.request('scene', 'snapshot-abort');
    },
    /**
     * 开始记录撤销操作
     */
    async beginRecording(nodeUuid) {
        return NexusMessage.request('scene', 'begin-recording', nodeUuid);
    },
    /**
     * 结束记录撤销操作
     */
    async endRecording(undoID) {
        return NexusMessage.request('scene', 'end-recording', undoID);
    },
    /**
     * 取消记录撤销操作
     */
    async cancelRecording(undoID) {
        return NexusMessage.request('scene', 'cancel-recording', undoID);
    },
    /**
     * 软重载场景
     */
    async softReload() {
        return NexusMessage.request('scene', 'soft-reload');
    },
    /**
     * 编辑器工具操作
     */
    /**
     * 切换 Gizmo 工具
     */
    async changeGizmoTool(toolName) {
        return NexusMessage.request('scene', 'change-gizmo-tool', toolName);
    },
    /**
     * 查询当前 Gizmo 工具名称
     */
    async queryGizmoToolName() {
        return NexusMessage.request('scene', 'query-gizmo-tool-name');
    },
    /**
     * 切换 Gizmo 枢轴点
     */
    async changeGizmoPivot(pivotName) {
        return NexusMessage.request('scene', 'change-gizmo-pivot');
    },
    /**
     * 查询当前 Gizmo 枢轴点名称
     */
    async queryGizmoPivot() {
        return NexusMessage.request('scene', 'query-gizmo-pivot');
    },
    /**
     * 查询视图模式（查看/选择）
     */
    async queryGizmoViewMode() {
        return NexusMessage.request('scene', 'query-gizmo-view-mode');
    },
    /**
     * 切换 Gizmo 坐标系
     */
    async changeGizmoCoordinate(type) {
        return NexusMessage.request('scene', 'change-gizmo-coordinate', type);
    },
    /**
     * 查询当前 Gizmo 坐标系
     */
    async queryGizmoCoordinate() {
        return NexusMessage.request('scene', 'query-gizmo-coordinate');
    },
    /**
     * 视图控制
     */
    /**
     * 切换 2D/3D 视图
     */
    async changeIs2D(is2D) {
        return NexusMessage.request('scene', 'change-is2D', is2D);
    },
    /**
     * 查询当前视图模式（2D/3D）
     */
    async queryIs2D() {
        return NexusMessage.request('scene', 'query-is2D');
    },
    /**
     * 设置网格可见性
     */
    async setGridVisible(visible) {
        return NexusMessage.request('scene', 'set-grid-visible', visible);
    },
    /**
     * 查询网格可见状态
     */
    async queryIsGridVisible() {
        return NexusMessage.request('scene', 'query-is-grid-visible');
    },
    /**
     * 设置图标 Gizmo 为 3D 或 2D
     */
    async setIconGizmo3d(is3D) {
        return NexusMessage.request('scene', 'set-icon-gizmo-3d', is3D);
    },
    /**
     * 查询图标 Gizmo 模式
     */
    async queryIsIconGizmo3d() {
        return NexusMessage.request('scene', 'query-is-icon-gizmo-3d');
    },
    /**
     * 图标 Gizmo 大小控制
     */
    /**
     * 设置图标 Gizmo 大小
     */
    async setIconGizmoSize(size) {
        return NexusMessage.request('scene', 'set-icon-gizmo-size', size);
    },
    /**
     * 查询图标 Gizmo 大小
     */
    async queryIconGizmoSize() {
        return NexusMessage.request('scene', 'query-icon-gizmo-size');
    },
    /**
     * 相机控制
     */
    /**
     * 聚焦编辑器相机到节点
     */
    async focusCamera(nodeUuids) {
        return NexusMessage.request('scene', 'focus-camera', nodeUuids);
    },
    /**
     * 应用场景相机位置和角度到选中的节点
     */
    async alignWithView() {
        return NexusMessage.request('scene', 'align-with-view');
    },
    /**
     * 应用选中节点的位置和角度到当前视图
     */
    async alignViewWithNode() {
        return NexusMessage.request('scene', 'align-view-with-node');
    },
    /**
     * 场景状态查询
     */
    /**
     * 查询当前场景的就绪状态
     */
    async queryIsReady() {
        return NexusMessage.request('scene', 'query-is-ready');
    },
    /**
     * 查询节点的转储数据
     */
    async queryNode(nodeUuid) {
        return NexusMessage.request('scene', 'query-node', nodeUuid);
    },
    /**
     * 查询组件的转储数据
     */
    async queryComponent(componentUuid) {
        return NexusMessage.request('scene', 'query-component', componentUuid);
    },
    /**
     * 查询节点树信息
     */
    async queryNodeTree(rootUuid) {
        return NexusMessage.request('scene', 'query-node-tree', rootUuid);
    },
    /**
     * 根据资源 UUID 查询节点
     */
    async queryNodesByAssetUuid(assetUuid) {
        return NexusMessage.request('scene', 'query-nodes-by-asset-uuid', assetUuid);
    },
    /**
     * 查询当前场景的脏状态
     */
    async queryDirty() {
        return NexusMessage.request('scene', 'query-dirty');
    },
    /**
     * 系统查询
     */
    /**
     * 查询所有类
     */
    async queryClasses(extendsFilter) {
        return NexusMessage.request('scene', 'query-classes');
    },
    /**
     * 查询所有组件
     */
    async queryComponents() {
        return NexusMessage.request('scene', 'query-components');
    },
    /**
     * 查询组件是否包含脚本
     */
    async queryComponentHasScript(scriptClassName) {
        return NexusMessage.request('scene', 'query-component-has-script', scriptClassName);
    }
};
/**
 * 资源数据库相关操作
 */
NexusMessage.Assets = {
    /**
     * 检查数据库是否已启动
     */
    async queryReady() {
        return NexusMessage.request('asset-db', 'query-ready');
    },
    /**
     * 创建新资源
     */
    async createAsset(url, content, options) {
        return NexusMessage.request('asset-db', 'create-asset', url, content, options);
    },
    /**
     * 导入资源文件或文件夹到数据库
     */
    async importAsset(source, target, options) {
        return NexusMessage.request('asset-db', 'import-asset', source, target, options);
    },
    /**
     * 复制资源
     */
    async copyAsset(source, target, options) {
        return NexusMessage.request('asset-db', 'copy-asset', source, target, options);
    },
    /**
     * 移动资源
     */
    async moveAsset(source, target, options) {
        return NexusMessage.request('asset-db', 'move-asset', source, target, options);
    },
    /**
     * 删除资源
     */
    async deleteAsset(url) {
        return NexusMessage.request('asset-db', 'delete-asset', url);
    },
    /**
     * 打开资源
     */
    async openAsset(urlOrUUID) {
        return NexusMessage.request('asset-db', 'open-asset', urlOrUUID);
    },
    /**
     * 保存资源
     */
    async saveAsset(urlOrUUID, content) {
        return NexusMessage.request('asset-db', 'save-asset', urlOrUUID, content);
    },
    /**
     * 保存资源元信息
     */
    async saveAssetMeta(urlOrUUID, content) {
        return NexusMessage.request('asset-db', 'save-asset-meta', urlOrUUID, content);
    },
    /**
     * 重新导入资源
     */
    async reimportAsset(urlOrUUID) {
        return NexusMessage.request('asset-db', 'reimport-asset', urlOrUUID);
    },
    /**
     * 刷新资源URL位置，删除文件将被销毁，新文件将被导入
     */
    async refreshAsset(urlOrUUID) {
        return NexusMessage.request('asset-db', 'refresh-asset', urlOrUUID);
    },
    /**
     * 查询资源路径
     */
    async queryPath(urlOrUUID) {
        return NexusMessage.request('asset-db', 'query-path', urlOrUUID);
    },
    /**
     * 查询资源URL
     */
    async queryUrl(uuidOrPath) {
        return NexusMessage.request('asset-db', 'query-url', uuidOrPath);
    },
    /**
     * 查询资源UUID
     */
    async queryUuid(urlOrUUID) {
        return NexusMessage.request('asset-db', 'query-uuid', urlOrUUID);
    },
    /**
     * 根据条件查询资源
     */
    async queryAssets(pattern, options) {
        return NexusMessage.request('asset-db', 'query-assets', pattern ? { pattern, ...options } : options);
    },
    /**
     * 查询资源基本信息
     */
    async queryAssetInfo(urlOrUUID) {
        return NexusMessage.request('asset-db', 'query-asset-info', urlOrUUID);
    },
    /**
     * 查询资源元信息
     */
    async queryAssetMeta(urlOrUUID) {
        return NexusMessage.request('asset-db', 'query-asset-meta', urlOrUUID);
    },
    /**
     * 生成基于传入URL的新可用URL
     */
    async generateAvailableUrl(url) {
        return NexusMessage.request('asset-db', 'generate-available-url', url);
    },
    /**
     * 资源数据库事件监听
     */
    /**
     * 监听资源数据库准备就绪事件
     */
    onReady(callback) {
        NexusMessage.on('asset-db:ready', callback);
    },
    /**
     * 监听资源数据库关闭事件
     */
    onClose(callback) {
        NexusMessage.on('asset-db:close', callback);
    },
    /**
     * 监听资源添加事件
     */
    onAssetAdd(callback) {
        NexusMessage.on('asset-db:asset-add', callback);
    },
    /**
     * 监听资源修改事件
     */
    onAssetChange(callback) {
        NexusMessage.on('asset-db:asset-change', callback);
    },
    /**
     * 监听资源删除事件
     */
    onAssetDelete(callback) {
        NexusMessage.on('asset-db:asset-delete', callback);
    }
};
/**
 * 偏好设置相关操作
 */
NexusMessage.Preferences = {
    /**
     * 打开偏好设置面板
     */
    async openSettings(tab, ...args) {
        return NexusMessage.request('preferences', 'open-settings', tab, ...args);
    },
    /**
     * 查询偏好设置配置
     */
    async queryConfig(name, path, type) {
        return NexusMessage.request('preferences', 'query-config', name, path, type);
    },
    /**
     * 设置偏好设置配置
     */
    async setConfig(name, path, value, type) {
        return NexusMessage.request('preferences', 'set-config', name, path, value, type);
    }
};
/**
 * 项目相关操作
 */
NexusMessage.Project = {
    /**
     * 打开项目设置面板
     */
    async openSettings(name, tab, ...args) {
        return NexusMessage.request('project', 'open-settings', name, tab, ...args);
    },
    /**
     * 查询项目配置
     */
    async queryConfig(name, path, type) {
        return NexusMessage.request('project', 'query-config', name, path, type);
    },
    /**
     * 设置项目配置
     */
    async setConfig(name, path, value) {
        return NexusMessage.request('project', 'set-config', name, path, value);
    },
    /**
     * 保存项目
     */
    async save() {
        return NexusMessage.request('project:save');
    },
    /**
     * 构建项目
     */
    async build(options) {
        return NexusMessage.request('project:build', options);
    }
};
/**
 * 控制台相关操作
 */
NexusMessage.Console = {
    /**
     * 输出日志
     */
    log(message, ...args) {
        NexusMessage.send('console:log', { message, args });
    },
    /**
     * 输出警告
     */
    warn(message, ...args) {
        NexusMessage.send('console:warn', { message, args });
    },
    /**
     * 输出错误
     */
    error(message, ...args) {
        NexusMessage.send('console:error', { message, args });
    },
    /**
     * 清空控制台
     */
    clear() {
        NexusMessage.send('console:clear');
    }
};
/**
 * 通知系统
 */
NexusMessage.Notification = {
    /**
     * 显示信息通知
     */
    info(title, message) {
        NexusMessage.send('notification:info', { title, message });
    },
    /**
     * 显示成功通知
     */
    success(title, message) {
        NexusMessage.send('notification:success', { title, message });
    },
    /**
     * 显示警告通知
     */
    warn(title, message) {
        NexusMessage.send('notification:warn', { title, message });
    },
    /**
     * 显示错误通知
     */
    error(title, message) {
        NexusMessage.send('notification:error', { title, message });
    }
};
/**
 * 对话框系统
 */
NexusMessage.Dialog = {
    /**
     * 显示确认对话框
     */
    async confirm(title, message) {
        const result = await NexusMessage.request('dialog:confirm', { title, message });
        return result === 'confirm';
    },
    /**
     * 显示输入对话框
     */
    async prompt(title, message, defaultValue) {
        return NexusMessage.request('dialog:prompt', { title, message, defaultValue });
    },
    /**
     * 显示文件选择对话框
     */
    async openFile(title, filters) {
        return NexusMessage.request('dialog:open-file', { title, filters });
    },
    /**
     * 显示文件保存对话框
     */
    async saveFile(title, defaultPath) {
        return NexusMessage.request('dialog:save-file', { title, defaultPath });
    }
};
/**
 * 服务器相关操作
 */
NexusMessage.Server = {
    /**
     * 查询 IP 列表
     */
    async queryIpList() {
        return NexusMessage.request('server', 'query-ip-list');
    },
    /**
     * 获取排序的 IP 列表
     */
    async querySortIpList() {
        return NexusMessage.request('server', 'query-sort-ip-list');
    },
    /**
     * 查询当前编辑器服务器端口号
     */
    async queryPort() {
        return NexusMessage.request('server', 'query-port');
    }
};
/**
 * 构建器相关操作
 */
NexusMessage.Builder = {
    /**
     * 打开构建面板
     */
    async open() {
        return NexusMessage.request('builder', 'open');
    },
    /**
     * 查询构建进程是否就绪
     */
    async queryWorkerReady() {
        return NexusMessage.request('builder', 'query-worker-ready');
    },
    /**
     * 构建进程事件监听
     */
    /**
     * 监听构建进程就绪事件
     */
    onWorkerReady(callback) {
        NexusMessage.on('build-worker:ready', callback);
    },
    /**
     * 监听构建进程关闭事件
     */
    onWorkerClosed(callback) {
        NexusMessage.on('build-worker:closed', callback);
    }
};
/**
 * 参考图像相关操作
 */
NexusMessage.ReferenceImage = {
    /**
     * 添加参考图像
     */
    async addImage(paths) {
        return NexusMessage.request('reference-image', 'add-image', paths);
    },
    /**
     * 移除参考图像
     */
    async removeImage(paths) {
        return NexusMessage.request('reference-image', 'remove-image', paths);
    },
    /**
     * 切换参考图像
     */
    async switchImage(path, specifySceneUUID) {
        return NexusMessage.request('reference-image', 'switch-image', path);
    },
    /**
     * 设置参考图像数据
     */
    async setImageData(options) {
        return NexusMessage.request('reference-image', 'set-image-data', {
            path: options.path,
            x: options.x || 0,
            y: options.y || 0,
            sx: options.sx || 1,
            sy: options.sy || 1,
            opacity: options.opacity || 1
        });
    },
    /**
     * 查询参考图像配置
     */
    async queryConfig() {
        return NexusMessage.request('reference-image', 'query-config');
    },
    /**
     * 查询当前参考图像数据
     */
    async queryCurrent() {
        return NexusMessage.request('reference-image', 'query-current');
    },
    /**
     * 刷新参考图像
     */
    async refresh() {
        return NexusMessage.request('reference-image', 'refresh');
    }
};
/**
 * 消息装饰器 - 自动注册消息处理器
 */
function Message(messageName) {
    return function (target, propertyKey, descriptor) {
        const originalMethod = descriptor.value;
        // 在类实例化时注册消息处理器
        const originalConstructor = target.constructor;
        if (!originalConstructor._nexusMessageHandlers) {
            originalConstructor._nexusMessageHandlers = [];
        }
        originalConstructor._nexusMessageHandlers.push({
            message: messageName,
            method: propertyKey,
            handler: originalMethod
        });
        return descriptor;
    };
}
/**
 * 自动注册类中的消息处理器
 */
function registerMessageHandlers(instance) {
    const constructor = instance.constructor;
    const handlers = constructor._nexusMessageHandlers;
    if (handlers) {
        handlers.forEach(({ message, handler }) => {
            NexusMessage.on(message, handler.bind(instance));
        });
    }
}
